import { useEffect } from 'react';
import type { FC } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { PageType } from '@vgtv/api-client/lib/page';
import type { SvpCategory } from '@vgtv/api-client/lib/svp_category';

import { PageComponents } from '../components/PageComponents/PageComponents';
import { PageMetadata } from '../components/PageMetadata/PageMetadata';
import { PageHeaderSplash } from '../components/PageHeaderSplash/PageHeaderSplash';
import { pageStartsWithSplash } from '../shared/helpers/pageHelpers';
import { getPath, routeNames } from '../shared/helpers/routes';
import { PageSpinner } from '../components/PageSpinner/PageSpinner';
import { isCategoryRestricted } from '../shared/helpers/categoryHelpers';
import { usePageViewTracking } from '../components/utils/usePageViewTracking';
import { replaceUrl } from '../helpers/replaceUrl';
import { CategoryPageStructuredData } from '../components/StructuredData/CategoryPageStructuredData';
import { useVendor } from '../components/VendorPovider/VendorPovider';
import { useQuery } from '../components/utils/useQuery';

import { usePageConfig } from './usePageConfig';
import { NotFoundPage } from './NotFoundPage';

const CategoryPage: FC = () => {
  const { categoryId: categoryIdParam } = useParams<{ categoryId?: string }>();
  const { categoryId: id } = useQuery<'categoryId'>();
  const { vendor } = useVendor();

  const categoryId = (categoryIdParam || id) as string;

  const history = useHistory();

  const { pageConfig, isFetching, isFetched } = usePageConfig({
    identifier: parseInt(categoryId!, 10) as SvpCategory['id'],
    pageType: PageType.CATEGORY,
    vendor,
  });

  useEffect(() => {
    if (
      pageConfig &&
      pageConfig.category &&
      !isCategoryRestricted(vendor, pageConfig.category)
    ) {
      replaceUrl(
        history,
        getPath(routeNames.category, {
          vendor,
          category: pageConfig.category,
        })
      );
    }
  }, [pageConfig, history, vendor]);

  usePageViewTracking({
    pageType: PageType.CATEGORY,
    entity: pageConfig ? pageConfig.category : undefined,
  });

  if (!pageConfig) {
    if (isFetching) {
      return <PageSpinner />;
    }

    if (!isFetching && isFetched) {
      return <NotFoundPage />;
    }

    return null;
  }

  if (isCategoryRestricted(vendor, pageConfig.category)) {
    return <NotFoundPage />;
  }

  const { components } = pageConfig;

  const startsWithSplash = pageStartsWithSplash(pageConfig);

  return (
    <>
      <PageMetadata
        entity={pageConfig.category}
        pageType={PageType.CATEGORY}
        overrides={pageConfig.meta}
      />
      {!startsWithSplash && <PageHeaderSplash pageConfiguration={pageConfig} />}
      <PageComponents componentsConfig={components} />
      <CategoryPageStructuredData pageConfig={pageConfig} />
    </>
  );
};

export { CategoryPage };
